/**
 * Trilha de auditoria da configuração das marcas (A104).
 *
 * Responde "quem mudou isto, quando, e o que estava antes?". Em 07/10/2026 a
 * lista branca foi ligada em duas marcas e os conteúdos sumiram para 137
 * pacientes; os logs do Cloud Run mostravam `PUT /api/partners/default`, mas
 * não a pessoa nem o campo — e somem em 30 dias.
 *
 * Um registro por escrita numa coleção auditada, feita por uma sessão
 * administrativa. Só se acrescenta: a API não edita nem apaga registro.
 * Quem lê é a mantenedora, na página da marca — não o portal da marca.
 *
 * `antes` e `depois` são JSON em texto, não o valor cru: o Firestore recusa
 * lista dentro de lista, e um campo de configuração pode ter qualquer forma.
 * Valor de campo sensível (token, segredo) chega como `SENSIVEL`.
 */
import { z } from 'zod';

export const OperacaoAuditadaEnum = z.enum(['criar', 'alterar', 'apagar']);
export type OperacaoAuditada = z.infer<typeof OperacaoAuditadaEnum>;

/** Marcador do valor de campo sensível: diz que mudou, sem dizer para quê. */
export const VALOR_SENSIVEL = 'SENSIVEL';

export const MudancaAuditadaSchema = z.object({
  /** Caminho do campo, com ponto: `contentPreferences.enableContentExceptions`. */
  campo: z.string(),
  /** JSON do valor anterior; `null` = o campo não existia. */
  antes: z.string().nullable(),
  /** JSON do valor novo; `null` = o campo deixou de existir. */
  depois: z.string().nullable(),
  /** Um dos lados era grande demais e foi gravado como resumo: não dá para desfazer. */
  resumido: z.boolean().optional(),
});
export type MudancaAuditada = z.infer<typeof MudancaAuditadaSchema>;

export const AtorAuditadoSchema = z.object({
  /** O `id` da conta (o campo, não o ID do documento). */
  accountId: z.string(),
  /** Nome e e-mail no instante da ação: a pessoa pode mudar de nome ou sair. */
  nome: z.string().nullable().default(null),
  email: z.string().nullable().default(null),
  /** Papel da sessão: `MAINTAINER`, `BRAND_ADMIN`... ou o legado (`INTERNAL`). */
  papel: z.string().nullable().default(null),
  /** A sessão governa o ecossistema — a mantenedora agindo na marca. */
  mantenedora: z.boolean().default(false),
});
export type AtorAuditado = z.infer<typeof AtorAuditadoSchema>;

export const RegistroDeAuditoriaSchema = z.object({
  id: z.string(),
  em: z.coerce.date(),
  /**
   * As marcas a que o registro pertence. Lista porque a concessão é de duas:
   * a que concede e a que recebe veem a mesma linha.
   */
  brandIds: z.array(z.string()),
  colecao: z.string(),
  documentoId: z.string(),
  operacao: OperacaoAuditadaEnum,
  /** Como o documento se chama para gente (o nome da marca, o papel do vínculo). */
  rotulo: z.string().nullable().default(null),
  ator: AtorAuditadoSchema,
  mudancas: z.array(MudancaAuditadaSchema).default([]),
  /** Mudanças além do teto por registro, contadas e não gravadas. */
  mudancasOmitidas: z.number().int().nonnegative().default(0),
  /** A tela de onde veio a ação (caminho do `Referer`), quando o navegador manda. */
  tela: z.string().nullable().default(null),
  /** Método e rota da requisição: `PUT /api/partners/default`. */
  rota: z.string().nullable().default(null),
  requestId: z.string().nullable().default(null),
  /** Este registro é o desfazer de outro (A106): o id dele. */
  desfaz: z.string().nullable().default(null),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type RegistroDeAuditoria = z.infer<typeof RegistroDeAuditoriaSchema>;

/**
 * O que a mantenedora pode desfazer pelo histórico (A106).
 *
 * Desfazer grava o valor anterior de volta pelo repositório — com o schema,
 * mas sem a regra de negócio da rota que fez a mudança. Por isso a lista é
 * fechada, por coleção, e só tem campo cuja escrita não faz mais nada:
 *
 * - fora: funcionalidades (desligar o conteúdo próprio apaga os cursos da
 *   marca; a coerência entre elas é checada na rota), endereço e documento
 *   (unicidade), política de chat (coerência com as funcionalidades),
 *   domínios (o estado é do Firebase Hosting), integrações, vagas, situação;
 * - fora: equipe, convites, link de convite e concessões — são acesso, e se
 *   desfazem pela própria seção, que avisa o que muda para quem;
 * - fora: a liberação de conteúdo da paciente — é dado de paciente, e a
 *   mantenedora governa, não opera (A024);
 * - fora: situação e versão publicada do item de cuidado — publicar valida a
 *   definição e arquivar mexe nos lembretes; isso é da tela do item.
 *
 * Caminho vale por prefixo: `branding.colors` cobre as cinco cores.
 */
export const CAMPOS_DESFAZIVEIS: Record<string, string[]> = {
  partners: [
    'name',
    'contentPreferences',
    'branding.colors',
    'branding.logoHasOwnBackground',
    'onboarding',
    'homeScreenLayout',
    'contentScreenLayout',
    'careScreenLayout',
    'appUiPreferences',
  ],
  careItems: ['internalName', 'title', 'description', 'journey', 'audience', 'activation', 'priority', 'allowsNewResponse', 'estimatedMinutes'],
  careForms: ['title', 'description'],
  trails: ['title', 'description', 'coverUrl', 'visibility', 'targetAudience', 'conditions', 'certificate'],
  careVariables: ['label', 'description'],
  careToolTexts: ['opening', 'description'],
};

const cobre = (prefixos: string[], campo: string) => prefixos.some((p) => campo === p || campo.startsWith(`${p}.`));

/**
 * O registro pode ser desfeito? Regra só do registro — se o campo mudou
 * depois, quem descobre é a API, na hora, lendo o documento.
 */
export function podeDesfazer(r: Pick<RegistroDeAuditoria, 'colecao' | 'operacao' | 'mudancas' | 'mudancasOmitidas'>): { pode: true } | { pode: false; motivo: string } {
  const prefixos = CAMPOS_DESFAZIVEIS[r.colecao];
  if (!prefixos) return { pode: false, motivo: 'Esta área se desfaz pela própria seção, que avisa o que muda para quem.' };
  if (r.operacao !== 'alterar') return { pode: false, motivo: 'Só alteração se desfaz por aqui; criar e apagar, não.' };
  if (r.mudancasOmitidas > 0 || r.mudancas.length === 0) return { pode: false, motivo: 'O registro não guardou todas as mudanças.' };
  for (const m of r.mudancas) {
    if (!cobre(prefixos, m.campo)) return { pode: false, motivo: `O campo ${m.campo} se desfaz pela própria seção.` };
    if (m.antes === VALOR_SENSIVEL || m.depois === VALOR_SENSIVEL) return { pode: false, motivo: 'O valor anterior é protegido e não foi guardado.' };
    if (m.resumido) return { pode: false, motivo: 'O valor anterior era grande demais e foi guardado só como resumo.' };
  }
  return { pode: true };
}
